import { useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import { DragTypes } from "../../models/DragTypes";
import {
  DropOffSlot,
  IDropOffDragItem,
  IDropOffLeadRow,
} from "../../models/DropOffPlanner";
import classNames from "../../utils/classNames";
import DropOffLeadCard from "./DropOffLeadCard";

interface DropOffSlotZoneProps {
  date: string;
  slot: DropOffSlot;
  label: string;
  leads: IDropOffLeadRow[];
  onDrop: (item: IDropOffDragItem, date: string, slot: DropOffSlot) => void;
}

export default function DropOffSlotZone({
  date,
  slot,
  label,
  leads,
  onDrop,
}: DropOffSlotZoneProps) {
  const { t } = useTranslation("leads");
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DragTypes.DROP_OFF_LEAD,
      canDrop: (item: IDropOffDragItem) =>
        !(item.currentScheduledOn?.startsWith(date) && item.currentSlot === slot),
      drop: (item: IDropOffDragItem) => onDrop(item, date, slot),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [date, slot, onDrop]
  );

  return (
    <div className="border-t border-gray-200 first:border-t-0">
      <div className="px-3 py-1 bg-white flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-800">
          {label}
        </span>
        <span className="text-[11px] text-gray-500">{leads.length}</span>
      </div>

      <div
        ref={drop}
        className={classNames(
          "p-2 space-y-2 min-h-[100px] transition-colors",
          isOver && canDrop ? "bg-primary-50 ring-2 ring-primary-400 ring-inset" : ""
        )}
      >
        {leads.length === 0 ? (
          <div className="h-20 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center text-xs text-gray-400">
            {t("Drag here")}
          </div>
        ) : (
          leads.map((lead) => <DropOffLeadCard key={lead.id} lead={lead} />)
        )}
      </div>
    </div>
  );
}
